<template>
  <main class="home-page container p-4 pt-2 text-sp-primary">
    <Search />
    <Digest />
    <Categories />
    <PromotedArticles />

    <transition name="spinner">
      <div v-if="chatMounting" class="spinner my-6 border border-white border-opacity-20 rounded-md bg-sp-secondary bg-opacity-70">
        <Loading>
          <div class="px-4 pt-0 pb-2 text-center">
            <span>{{ $t("loadingChat") }}</span>
            <div v-if="showSubmitFormFallback">
              <span>{{ $t("loadingChatStart") }}</span>
              <RouterLink
                to="/request"
                @click="logClick('main.createRequest', 'main.orbLoading')"
                class="text-sp-accent">
                <span>{{ $t("loadingChatLink") }}</span>
              </RouterLink>
              <span>{{ $t("loadingChatEnd") }}</span>
            </div>
          </div>
        </Loading>
      </div>
    </transition>
  </main>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useTranslate } from '../composables/useTranslate'
import { useRouter } from 'vue-router'
import { getUserData } from '../modules/collectData.js'
import { useLogger } from '../composables/useLogger'
import { fetchPortalUserId } from '../services/portalAuth'
import { measureNetwork } from '../services/networkTest'
import { initMeya } from '../services/meya/init'
import { onMeyaMounted } from '../services/meya/observers'
import { ZD_FIELDS } from '../config/integrations'
import { reportError } from '../utils/reporting'
import Digest from '../components/Digest.vue'
import Categories from '../components/Categories.vue'
import Search from '../components/Search.vue'
import PromotedArticles from '../components/PromotedArticles.vue'
import Loading from '../components/Loading.vue'

export default {
  name: 'Meya',
  components: {
    Search,
    Digest,
    Categories,
    PromotedArticles,
    Loading
  },
  setup() {
    const userData = getUserData()
    const router = useRouter()
    const { t, getTranslation } = useTranslate()
    const { logClick } = useLogger()

    const chatMounting = ref(true)
    const showSubmitFormFallback = ref(false)

    let stopMeyaMountedObserver = null
    let requestLinkTimer = null

    const buildPageContext = (portalUserId, networkStatus) => ({
      [ZD_FIELDS.IID]: userData.iid,
      [ZD_FIELDS.USER_METADATA]: {
        userAgent: navigator.userAgent,
        socId: userData.uid,
        localtime: new Date().toString(),
        resolution: { width: userData.width, height: userData.height },
        appVersion: userData.version,
        RAM: userData.ram
      },
      [ZD_FIELDS.LANGUAGE]: userData.lang,
      [ZD_FIELDS.PLATFORM]: userData.platform,
      [ZD_FIELDS.GAME]: userData.game,
      [ZD_FIELDS.SOC_ID]: userData.uid,
      [ZD_FIELDS.SIGNATURE]: userData.signature,
      [ZD_FIELDS.PORTAL_USER_ID]: portalUserId,
      networkStatus
    })

    const initChat = async () => {
      try {
        const portalUserId = await fetchPortalUserId({
          uid: userData.uid,
          platform: userData.platform,
          game: userData.game,
          signature: userData.signature
        })
        const networkStatus = await measureNetwork()
        initMeya({
          userId: portalUserId,
          pageContext: buildPageContext(portalUserId, networkStatus),
          launcherText: getTranslation('openChat')
        })
      } catch (err) {
        reportError(err, { source: 'HomeView.initChat' })
        router.push({ name: 'helpcenter' })
      }
    }

    onMounted(() => {
      initChat()

      requestLinkTimer = setTimeout(() => {
        showSubmitFormFallback.value = true
      }, 10000)

      stopMeyaMountedObserver = onMeyaMounted(() => {
        chatMounting.value = false
      })

      if (!sessionStorage.getItem('spVisited')) {
        logClick('main.spOpened', 'main.homePage')
        sessionStorage.setItem('spVisited', 'true')
      }
    })

    onBeforeUnmount(() => {
      stopMeyaMountedObserver?.()
      if (requestLinkTimer) clearTimeout(requestLinkTimer)
    })

    return { t, logClick, showSubmitFormFallback, chatMounting }
  }
}
</script>

<style>
.spinner-enter-active,
.spinner-leave-active {
  transition: opacity 0.5s;
}
.spinner-enter,
.spinner-leave-to {
  opacity: 0;
}

.orb-chat-mount [class^=MessageText-] {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--sp-accent);
  text-shadow: 0px 2px 0 rgba(0, 0, 0, 0.2);
}

.orb-chat-mount [class^=MessageButton-] {
  background: linear-gradient(to bottom right, #468F1C, #366A18);
  border-radius: 8px;
  position: relative;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
} 
.orb-chat-mount [class^=MessageButton-]::after {
  content: "";
  position: absolute;
  bottom: -11px;
  right: 10px;
  width: 0;
  height: 0;
  border-left: 12px solid transparent;
  border-top: 12px solid #366A18;
}

.orb-chat-mount [class^=LauncherRoot-] [class^=IconRoot-] {
  background: url('@/assets/icon-chat.png');
  background-size: contain;
  background-repeat: no-repeat;
}
.orb-chat-mount [class^=LauncherRoot-] [class^=IconRoot-] * {
  display: none;
}
.orb-chat-mount [class^=LauncherRoot-].is-loading [class^=MessageText-] {
  visibility: hidden;
  position: relative;
}
.orb-chat-mount [class^=LauncherRoot-].is-loading [class^=MessageText-]::after {
  content: '';
  visibility: visible;
  position: absolute;
  inset: 0;
  margin: auto;
  width: 18px;
  height: 18px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: launcher-spin 0.8s linear infinite;
}
@keyframes launcher-spin {
  to { transform: rotate(360deg); }
}

.orb-chat-mount [class^=Frame] {
  background: #1E273C;
  transform: none !important;
}

.orb-chat-mount [class^=Frame] [class*=HeaderRoot-] * {
  color: #CDD6EF;
}
.orb-chat-mount [class^=Frame] [class*=HeaderRoot-] {
  box-shadow: rgb(59, 72, 101) 0px -1px 0px 0px inset;
}
.orb-chat-mount [class^=Frame] [class*=HeaderRoot-][data-transparent] {
  box-shadow: none;
}
.orb-chat-mount [class^=Frame] [class*=HeaderRoot-] [class*=ButtonGroup-] [class*=ButtonRoot-]:focus {
  box-shadow: rgb(205, 214, 239) 0px 0px 0px 2px inset;
}

.orb-chat-mount [class^=Frame] [class*=HeaderRoot-] [class*=ButtonGroup-], 
.orb-chat-mount [class^=Frame] [class*=HeaderRoot-] [class*=ProgressRoot-], 
.orb-chat-mount [class^=Frame] [class*=HeaderRoot-] [class*=MilestonesRoot-],
.orb-chat-mount [class^=Frame] [class*=HeaderRoot-] [class*=TitleGroup-], 
.orb-chat-mount [class^=Frame] [class*=HeaderRoot-] [class*=TitleRoot-] [class*=IconRoot-], 
.orb-chat-mount [class^=Frame] [class*=HeaderRoot-] [class*=TitleRoot-] [class*=TitleText-] {
  backdrop-filter: blur(8px);
  border-radius: 12px;
  overflow: hidden;
}

.orb-chat-mount [class^=Frame] [class*=NameAttributionRoot-] {
  color: #5C6B8C;
  margin-top: 12px;
  margin-bottom: -8px;
}

.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-] {
  color: #CDD6EF;
}
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-] {
  box-shadow: rgb(59, 72, 101) 0px 0px 0px 1px inset, rgba(0, 0, 0, 0.03) 0px 2px 4px 0px;
}
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] {
  background-color: #273146;
}
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-] a {
  text-decoration: underline;
  color: #78CF5A;
}
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-] a strong {
  text-decoration: underline;
  color: #78CF5A;
}

.orb-chat-mount [class^=Frame] [class*=TextInfoRoot-] * {
  color: #CDD6EF;
}

.orb-chat-mount [class^=Frame] [class*=AskButtonsGroup-] [class*=ButtonRoot-] {
  color: #78CF5A;
  background-color: #2A4343;
}

.orb-chat-mount [class^=Frame] [class*=Widgets-] [class*=TextInputRoot-] [class*=InputBorderRoot-] {
  box-shadow: rgb(92, 107, 140) 0px 0px 0px 2px inset, rgba(0, 0, 0, 0.03) 0px 2px 4px 0px;
  background-color: transparent;
}
.orb-chat-mount [class^=Frame] [class*=Widgets-] [class*=TextInputRoot-] [class*=InputBorderRoot-][data-focus-within] {
  box-shadow: rgb(205, 214, 239) 0px 0px 0px 2px inset;
}
.orb-chat-mount [class^=Frame] [class*=Widgets-] [class*=TextInputRoot-] [class*=InputBorderRoot-] input {
  color: #CDD6EF;
}
.orb-chat-mount [class^=Frame] [class*=Widgets-] [class*=TextInputRoot-] [class*=InputBorderRoot-] input::placeholder {
  color: #5C6B8C;
}

.orb-chat-mount [class^=Frame] [class*=DateInputRoot-] label {
  color: #CDD6EF;
}
.orb-chat-mount [class^=Frame] [class*=DateInputRoot-] [class*=InputBorderRoot-] {
  background-color: #1E273C;
  box-shadow: rgb(92, 107, 140) 0px 0px 0px 2px inset, rgba(0, 0, 0, 0.03) 0px 2px 4px 0px;
}
.orb-chat-mount [class^=Frame] [class*=DateInputRoot-] [class*=InputBorderRoot-][data-focus-within] {
  box-shadow: rgb(205, 214, 239) 0px 0px 0px 2px inset;
}
.orb-chat-mount [class^=Frame] [class*=DateInputRoot-] [class*=InputBorderRoot-] input {
  color: #CDD6EF;
}
.orb-chat-mount [class^=Frame] [class*=DateInputRoot-] [class*=InputBorderRoot-] input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1);
  cursor: pointer;
}
.orb-chat-mount [class^=Frame] [class*=DateInputRoot-] [class*=InputBorderRoot-] [class*=Submit-]:disabled {
  color: #B8B8B8;
  background-color: #31511c;
}

.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] {
  margin-top: 16px;
}
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] a,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] a,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] a:hover,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] a:hover {
  color: #78CF5A;
}
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] a:active,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] a:active,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] a:visited,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] a:visited {
  color: #4E7342;
}

.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] {
  background: transparent;
  margin: 8px 64px 0 0;
}

.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] p,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] p,  
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] ul, 
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] ul,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] ol,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] ol {
  color: #CDD6EF;
}
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] ul,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] ol,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] ul,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] ol {
  padding-left: 1rem;
}

.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] ol,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] ol {
  list-style-type: auto;
}
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextOtherRoot-] strong,
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextSelfRoot-] strong {
  color: #FFFDED;
}

.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextInfoRoot-] {
  margin-top: 16px;
  box-shadow: none;
}
.orb-chat-mount [class^=Frame] [class*=TextBaseRoot-][class*=TextInfoRoot-] h2 {
  color: #CDD6EF;
}

.orb-chat-mount [class^=Frame] [class*=RowTile-] {
  background: #273146;
}
.orb-chat-mount [class^=Frame] [class*=RowTile-] [class*=Title-] {
  color: #FFE5B5;
}
.orb-chat-mount [class^=Frame] [class*=RowTile-] [class*=Description-] {
  color: #CDD6EF;
}
.orb-chat-mount [class^=Frame] [class*=AskTilesRowRoot-] {
  margin-top: 16px;
}
.orb-chat-mount [class^=Frame] [class*=AskTilesRowRoot-] [class*=IconRoot-] {
  color: #AAF790;
  background-color: rgba(106, 135, 87, 0.7);
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
}
.orb-chat-mount [class^=Frame] [class*=AskTilesRowRoot-] [class*=RatingIcon-] {
  background-color: transparent;
}

.orb-chat-mount [class^=Frame] [class*=FileBaseRoot-],
.orb-chat-mount [class^=Frame] [class*=FileOtherRoot-] {
  margin-top: 8px;
  background-color: #2A4343;
}
.orb-chat-mount [class^=Frame] [class*=FileBaseRoot-] [class*=IconRoot-],
.orb-chat-mount [class^=Frame] [class*=FileBaseRoot-] [class*=Filename-],
.orb-chat-mount [class^=Frame] [class*=FileOtherRoot-] [class*=IconRoot-],
.orb-chat-mount [class^=Frame] [class*=FileOtherRoot-] [class*=Filename-] {
  color: #78CF5A;
}

.orb-chat-mount [class^=Frame] [class*=QuickRepliesRoot-] [class*=QuickReply-] {
  margin: 16px 4px 0;
}
.orb-chat-mount [class^=Frame] [class*=QuickRepliesRoot-] [class*=QuickReply-]:not(:disabled) {
  color: #78CF5A;
  background-color: transparent;
  box-shadow: rgb(97, 169, 72) 0px 0px 0px 2px inset, rgba(0, 0, 0, 0.03) 0px 2px 4px 0px;
  margin: 16px 4px 0;
}
.orb-chat-mount [class^=Frame] [class*=QuickRepliesRoot-] [class*=QuickReply-]:disabled {
  color: #5C6B8C;
  background-color: transparent;
  box-shadow: rgb(92, 107, 140) 0px 0px 0px 2px inset, rgba(0, 0, 0, 0.03) 0px 2px 4px 0px;
  margin: 16px 4px 0;
}

.orb-chat-mount [class^=Frame] [class*=RowActionButton-] {
  color: #FFFDED;
  background-color: #497929;
}
.orb-chat-mount [class^=Frame] [class*=RowActionButton-]:disabled {
  color: #B8B8B8;
  background-color: #2c4919;
}
.orb-chat-mount [class^=Frame] [class*=ScrollBody-] {
  padding: 0 16px 0 62px; 
}
.orb-chat-mount [class^=Frame] [class*=ScrollBody-] [class*=ImageBaseRoot-] {
  margin-top: 16px;
}
.orb-chat-mount [class^=Frame] [class*=ScrollBody-] [class*=ButtonGroup-] [class*=ButtonRoot-] {
  color: #FFFDED;
  background-color: #52872E;
  margin-top: 8px; 
  margin-bottom: 16px;
}
.orb-chat-mount [class^=Frame] [class*=ButtonRoot-]:not([data-mode="standalone"]):focus {
  box-shadow: rgb(76, 148, 28) 0px 0px 0px 1px inset, 
  rgba(219, 233, 209, 0.5) 0px 0px 0px 1px, 
  rgba(0, 0, 0, 0.12) 0px 2px 6px 0px;
}
.orb-chat-mount [class^=Frame] [class*=ButtonRoot-]:focus {
  box-shadow: rgb(76, 148, 28) 0px 0px 0px 2px inset, 
  rgba(219, 233, 209, 0.5) 0px 0px 0px 2px, 
  rgba(0, 0, 0, 0.12) 0px 2px 6px 0px;
}
.orb-chat-mount [class^=Frame] [class*=AskButtonsRoot-] {
  margin-top: 16px;
}
.orb-chat-mount [class^=Frame] [class*=AskButtonsGroup-] [class*=ButtonRoot-]:not([data-mode="standalone"])[data-selected] {
  background-color: #253b3b;
}

.orb-chat-mount [class^=Frame] [class*=ComposerChatRoot-] [class*=SendText-] {
  color: #78CF5A;
}
.orb-chat-mount [class^=Frame] [class*=ComposerChatRoot-] [class*=SendText-]:disabled {
  color: #3B4864;
}
.orb-chat-mount [class^=Frame] [class*=ComposerChatRoot-] [class*=TextInput-] {
  color: #CDD6EF;
}
.orb-chat-mount [class^=Frame] [class*=ComposerChatRoot-] [class*=ExtraInput-],
.orb-chat-mount [class^=Frame] [class*=ComposerChatRoot-] [class*=TextMode-] textarea::placeholder {
  color: #3B4864;
}
.orb-chat-mount [class^=Frame] [class*=ComposerChatRoot-] {
  box-shadow: rgb(59, 72, 101) 0px 1px 0px 0px inset;
}
.orb-chat-mount [class^=Frame] [class*=ComposerChatRoot-] [class*=ExtraMode-] * {
  color: #CDD6EF;
}

.orb-chat-mount [class^=Frame] [class*=UploadText-],
.orb-chat-mount [class^=Frame] [class*=UploadProgressRoot-] {
  color: #CDD6EF;
}
.orb-chat-mount [class^=Frame] [class*=UploadProgress-] {
  background-color: #52872E;
}

.orb-chat-mount [class^=Frame] [class*=Separator-] {
  margin-top: 16px;
  background-color: #3B4864;
}

.orb-chat-mount [class^=Frame] [class*=BottomPadding-] {
  padding-bottom: 16px;
}

.orb-chat-mount [class^=Frame] [class*=PageRoot-] label {
  color: #CDD6EF;
}
.orb-chat-mount [class^=Frame] [class*=ChoiceInputRoot-] [class*=ChoiceList-] {
  box-shadow: rgb(92, 107, 140) 0px 0px 0px 2px inset, rgba(0, 0, 0, 0.03) 0px 2px 4px 0px;
}
.orb-chat-mount [class^=Frame] [class*=ChoiceInputRoot-] [class*=ChoiceList-] [class*=ChoiceButton-] {
  border-color: #3B4864;
}
.orb-chat-mount [class^=Frame] [class*=ChoiceInputRoot-] [class*=ChoiceList-] [class*=ChoiceButton-]:focus {
  background: transparent;
}
.orb-chat-mount [class^=Frame] [class*=ChoiceInputRoot-] [class*=ChoiceList-] [class*=ChoiceButton-] svg {
  stroke: #CDD6EF;
}

.orb-chat-mount [class^=Frame] [class*=HistoryRoot-],
.orb-chat-mount [class^=Frame] [class*=PageRoot-] {
  scrollbar-color: #CDD6EF transparent;
}
.orb-chat-mount [class^=Frame] [class*=HistoryRoot-]::-webkit-scrollbar-track,
.orb-chat-mount [class^=Frame] [class*=PageRoot-]::-webkit-scrollbar-track {
  background: transparent;
}
</style>